import { createSlice } from "@reduxjs/toolkit";
import { EligibilityTypes } from "../constants/eligibility-types";
import { ExistingCustomerCardSelectionType } from "../constants/existing-customer-card-selection-type";
import { CreditCardModalStatus } from "../constants/credit-card-modal-status";
import { ProductTypes } from "../constants/product-types";

/**
 * @description Handle all product selection related state changes
 * @param state
 */
const handleToggle = (state) => {
  state.isHasSaving = false;
  state.isHasCreditCard = false;
  state.applyNowBtnVisible = false;
  for (let key in state.productCategoryData) {
    for (let i in state.productCategoryData[key].product_list) {
      /** Check if the product is active */
      if (state.productCategoryData[key].product_list[i].isActive) {
        /** Check if the product is credit card */
        if (state.productCategoryData[key].Id === ProductTypes.CREDIT_CARD_ID) {
          state.isHasCreditCard = true;
          state.companionCardImg = state.productCategoryData[key].product_list[i].ImageUrl;
        }

        /** Check if the product is saving account */
        if (state.productCategoryData[key].Id === ProductTypes.SAVINGS_ACCOUNTS_ID) {
          state.isHasSaving = true;
          if (
            state.productCategoryData[key].product_list[i]?.IsMultipleCUR === "Y" &&
            state.productCategoryData[key].product_list[i].ProductCode === state.tempSelectedProduct?.ProductCode
          ) {
            state.isCreditCardModalOpen = true;
            state.creditCardModalStatus = CreditCardModalStatus.FOREIGN_CURRENCY;
          }
        }
        state.applyNowBtnVisible = true;
      }
    }
  }
};

/**
 * @description Handle the selected product item and toggle the active state of the product item and set the apply now button visibility status
 * @param state
 * @param payload
 */
const handleSelectedProduct = (state, payload) => {
  for (let key in state.productCategoryData) {
    for (let i in state.productCategoryData[key].product_list) {
      /** If all product is clear then clear all product active status of credit card */
      if (payload.isAllProductClear && state.productCategoryData[key].Id === ProductTypes.CREDIT_CARD_ID) {
        state.productCategoryData[key].product_list[i].isActive = false;
      }

      /** Iterate each product list and get the product code */
      if (state.productCategoryData[key].product_list[i].ProductCode === payload.ProductCode) {
        /** Toggle the active state */
        state.productCategoryData[key].product_list[i].isActive =
          !state.productCategoryData[key].product_list[i].isActive;
      }
      handleToggle(state);
    }
  }
  state.productCategoryData = newData.concat(state.productCategoryData);
};

const initialState = {
  isProductDetailsModalOpen: false,
  productReadMore: null,
  applyNowBtnVisible: false,
  isIncomeRangeIsApplicable: EligibilityTypes.NONE,
  productCategoryData: [],
  existingCustomerCardSelectionType: ExistingCustomerCardSelectionType.NONE,
  companionCardLimitOptions: null,
  companionCardLimit: null,
  isNewCustomerCompanionCardRequest: null,
  primaryCardData: [],
  isCreditCardModalOpen: false,
  creditCardModalStatus: CreditCardModalStatus.NONE,
  isHasSaving: false,
  isHasCreditCard: false,
  supNicRelatedInfo: null,
  companionCardImg: null,
  selectedCompanionCardImg: null,
  accountList: [],
  tempSelectedProduct: null,
};
let newData = [];
const productSelectionReducer = createSlice({
  name: "productSelectionReducer",
  initialState: initialState,
  reducers: {
    clearProductSelectionStore: () => initialState,
    setIsProductDetailsModalOpen: (state, { payload }) => {
      state.isProductDetailsModalOpen = payload;
    },
    setProductReadMore: (state, { payload }) => {
      state.productReadMore = payload;
      state.isProductDetailsModalOpen = true;
    },
    setApplyNowBtnVisible: (state, { payload }) => {
      state.applyNowBtnVisible = payload;
    },
    setIsIncomeRangeIsApplicable: (state, { payload }) => {
      state.isIncomeRangeIsApplicable = payload;
    },
    setAllProductCategory: (state, { payload }) => {
      state.productCategoryData = payload;
    },
    setSelectedProductItem: (state, { payload }) => {
      handleSelectedProduct(state, payload);
    },
    setCompanionProductSelect: (state, { payload }) => {
      for (let key in state.productCategoryData) {
        for (let i in state.productCategoryData[key].companionCardList) {
          state.productCategoryData[key].companionCardList[i].isActive = false;

          /** Iterate each product list and get the product code */
          if (state.productCategoryData[key].companionCardList[i].ProductCode === payload.ProductCode) {
            /** Toggle the active state */
            state.productCategoryData[key].companionCardList[i].isActive =
              !state.productCategoryData[key].companionCardList[i].isActive;
          }
          if (state.productCategoryData[key].companionCardList[i].isActive) {
            state.selectedCompanionCardImg = state.productCategoryData[key].companionCardList[i].ImageUrl;
          }
        }
      }
    },
    setPrimaryCardData: (state, { payload }) => {
      state.primaryCardData = payload;
    },
    setExistingCustomerCardSelectionType: (state, { payload }) => {
      state.existingCustomerCardSelectionType = payload;
    },
    setCompanionCardLimitOptions: (state, { payload }) => {
      state.companionCardLimitOptions = payload;
    },
    setCompanionCardLimit: (state, { payload }) => {
      state.companionCardLimit = payload;
    },
    setIsNewCustomerCompanionCardRequest: (state, { payload }) => {
      state.isNewCustomerCompanionCardRequest = payload;
    },
    setIsCreditCardModalOpen: (state, { payload }) => {
      state.isCreditCardModalOpen = payload;
    },
    setCreditCardModalStatus: (state, { payload }) => {
      state.creditCardModalStatus = payload;
    },
    setIsHasSaving: (state, { payload }) => {
      state.isHasSaving = payload;
    },
    setIsHasCreditCard: (state, { payload }) => {
      state.isHasCreditCard = payload;
    },
    setSupNicRelatedInfo: (state, { payload }) => {
      state.supNicRelatedInfo = payload;
    },
    setPortalEligibleProduct: (state, { payload }) => {
      state.productCategoryData.forEach((item) => {
        if (item.product_list && item.product_list.length > 0) {
          item.product_list.forEach((product) => {
            const hasExistingProduct = payload.find((i) => i.Productcode === product.ProductCode);
            if (hasExistingProduct) {
              handleSelectedProduct(state, {
                ProductCode: hasExistingProduct.Productcode,
                isAllProductClear: false,
              });
            }
          });
        }
      });
    },
    updateProductCategoryData: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === payload.id);
      if (category) {
        category.product_list = payload.productList;
        category.isActive = true;
      }

      for (let key in state.productCategoryData) {
        if (
          state.existingCustomerCardSelectionType === ExistingCustomerCardSelectionType.SUPPLEMENTARY_CARD &&
          state.productCategoryData[key].Id === ProductTypes.CREDIT_CARD_ID
        ) {
          for (let i in state.productCategoryData[key].product_list) {
            state.productCategoryData[key].product_list[i].isActive = true;
          }
        }
      }
      handleToggle(state);
    },
    handleCategoryStatus: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === payload.Id);
      if (category) {
        category.isActive = !category.isActive;
        category.product_list = [];
        handleToggle(state);
      }
    },
    setCompanionCardList: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === ProductTypes.CREDIT_CARD_ID);
      category.companionCardList = payload;
    },
    removeMainProduct: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === payload.CategoryCode);
      const product = category.product_list.find((product) => product.ProductCode === payload.ProductCode);
      if (payload.CategoryCode === ProductTypes.CREDIT_CARD_ID) {
        category.companionCardList = [];
      }
      product.isActive = false;
      handleToggle(state);
    },
    removeMultipleCurrencyThumbCard: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === payload.product.CategoryCode);
      const product = category.product_list.find((product) => product.ProductCode === payload.product.ProductCode);
      product.foreignCurrency = product.foreignCurrency.filter((item) => item !== payload.remove);
    },
    removeCompanionProduct: (state, { payload }) => {
      const category = state.productCategoryData.find((item) => item.Id === payload.CategoryCode);
      const product = category.companionCardList.find((product) => product.ProductCode === payload.ProductCode);
      product.isActive = false;
      handleToggle(state);
    },
    setAccountList: (state, { payload }) => {
      state.accountList = payload;
    },
    setTempSelectedProduct: (state, { payload }) => {
      state.tempSelectedProduct = payload;
    },
    updateForeignCurrency: (state, { payload }) => {
      const hasSavingCategory = state.productCategoryData.find((item) => item.Id === ProductTypes.SAVINGS_ACCOUNTS_ID);
      if (hasSavingCategory) {
        const product = hasSavingCategory?.product_list?.find(
          (item) => item.isActive === true && item.ProductCode === state.tempSelectedProduct.ProductCode,
        );
        if (product) {
          product.foreignCurrency = payload;
        }
      }
    },
  },
});

export default productSelectionReducer;
